import { Permission, PermissionsGroupId, PermissionsGroupRole } from '../../API';
import { useRecoilValue } from 'recoil';
import { withCurrentUser } from '../../state/auth';
import {
  getPermissionsGroupsWithDeleteAccess,
  getPermissionsGroupsWithPublishAccess,
  getPermissionsGroupsWithReadAccess,
  getPermissionsGroupsWithUpsertAccess
} from '../../utils/permissionsGroup';
import { useMemo } from 'react';
import { HomepageOptions } from '../../state/theme';
import { getPermissionsGroupsHomepageOption } from '../../utils/permissionsHomepageOption';
import { uniq } from 'lodash-es';

type ReturnType = {
  canDelete: boolean;
  canPublish: boolean;
  canRead: boolean;
  canSave: boolean;
  canManageUsers: boolean;
  isAdmin?: boolean;
};

interface PermissionsGuardProps {
  homepageOption: HomepageOptions;
}

export function usePermissionsGuard(props?: PermissionsGuardProps): ReturnType {
  const { homepageOption } = props || {};

  const currentUser = useRecoilValue(withCurrentUser);
  const userPermissionsGroups = currentUser?.permissionsGroups?.flatMap((group) => group.permissions);
  const homepageOptionPermissionsGroups = getPermissionsGroupsHomepageOption(homepageOption);

  const isAdmin = useMemo(
    () =>
      currentUser?.permissionsGroups?.some(
        (group) => group.group === PermissionsGroupId.ADMIN && group.role === PermissionsGroupRole.ADMIN
      ),
    [currentUser?.permissionsGroups]
  );

  const canSave = useMemo(() => {
    if (isAdmin) {
      return true;
    }
    if (userPermissionsGroups?.length && homepageOptionPermissionsGroups.length) {
      const allPermissionsByHomepageOption = homepageOptionPermissionsGroups.reduce((acc, group) => {
        const permissionsGroupWithUpsert = getPermissionsGroupsWithUpsertAccess(group) || [];
        return [...acc, ...permissionsGroupWithUpsert];
      }, [] as Permission[]);
      const permissionsGroupWithUpsert = uniq(allPermissionsByHomepageOption);

      return userPermissionsGroups?.some((group: Permission) => permissionsGroupWithUpsert?.includes(group));
    }

    return false;
  }, [userPermissionsGroups, homepageOptionPermissionsGroups]);

  const canPublish = useMemo(() => {
    if (isAdmin) {
      return true;
    }
    if (userPermissionsGroups?.length && homepageOptionPermissionsGroups.length) {
      const allPermissionsByHomepageOption = homepageOptionPermissionsGroups.reduce((acc, group) => {
        const permissionsGroupWithPublish = getPermissionsGroupsWithPublishAccess(group) || [];
        return [...acc, ...permissionsGroupWithPublish];
      }, [] as Permission[]);
      const permissionsGroupWithPublish = uniq(allPermissionsByHomepageOption);

      return userPermissionsGroups?.some((group: Permission) => permissionsGroupWithPublish?.includes(group));
    }

    return false;
  }, [userPermissionsGroups, homepageOptionPermissionsGroups]);

  const canDelete = useMemo(() => {
    if (isAdmin) {
      return true;
    }
    if (userPermissionsGroups?.length && homepageOptionPermissionsGroups.length) {
      const allPermissionsByHomepageOption = homepageOptionPermissionsGroups.reduce((acc, group) => {
        const permissionsGroupWithDelete = getPermissionsGroupsWithDeleteAccess(group) || [];
        return [...acc, ...permissionsGroupWithDelete];
      }, [] as Permission[]);
      const permissionsGroupWithDelete = uniq(allPermissionsByHomepageOption);

      return userPermissionsGroups?.some((group: Permission) => permissionsGroupWithDelete?.includes(group));
    }

    return false;
  }, [userPermissionsGroups, homepageOptionPermissionsGroups]);

  const canRead = useMemo(() => {
    if (isAdmin) {
      return true;
    }
    if (userPermissionsGroups?.length && homepageOptionPermissionsGroups.length) {
      const allPermissionsByHomepageOption = homepageOptionPermissionsGroups.reduce((acc, group) => {
        const permissionsGroupWithRead = getPermissionsGroupsWithReadAccess(group) || [];
        return [...acc, ...permissionsGroupWithRead];
      }, [] as Permission[]);
      const permissionsGroupWithRead = uniq(allPermissionsByHomepageOption);

      return userPermissionsGroups?.some((group: Permission) => permissionsGroupWithRead?.includes(group));
    }

    return false;
  }, [userPermissionsGroups, homepageOptionPermissionsGroups]);

  const canManageUsers =
    useMemo(
      () =>
        currentUser?.permissionsGroups?.some(
          (group) => group.group === PermissionsGroupId.ADMIN && group.role === PermissionsGroupRole.ADMIN
        ),
      [currentUser?.permissionsGroups]
    ) || false;

  return { canDelete, canPublish, canRead, canSave, canManageUsers, isAdmin };
}
